import { createSelector } from '@ngrx/store';
import { floorplanSchedules } from './schedules.selectors';
import { floorplanFilter, getFloorplanRequirements } from './filter.selectors';

import { TimelineRoomGroup, Day, FloorplanFilter, Requirement } from '../models';
import { resourcesLoading, storeData, timelineSizes } from './timeline.selectors';

import { differenceInMinutes, endOfDay, getDayOfYear, getHours, getMinutes, startOfDay } from 'date-fns';
import { AssistResourceTypeInfoBM } from '@cue/api';
import { CalendarAvailability } from '@cue/calendars';
import { ConfigService } from '../services';
import { daysBetweemDates, mixColors, patchSchedules, scheduleHelper } from '../utils';
import { TranslocoService } from '@jsverse/transloco';
import { localized } from '@cue/translate';

const getLocationNames = (name: string | string[] | undefined): string[] =>
  (Array.isArray(name) ? name : name ? [name] : []).filter((value) => value?.length > 0);

const getOverpatchedAvailabilityView = (
  availabilityView: CalendarAvailability[],
  filter: FloorplanFilter,
  otherAvailabilityViews: string[][],
  maxUt: number
) => {
  // Patch availability view regarding maxUT
  const maxCount = otherAvailabilityViews.length + 1;
  const maxAvailableCount = Math.floor((maxCount * maxUt) / 100);

  for (let i = 0; i < availabilityView.length; i++) {
    let count = 0;
    for (let j = 0; j < otherAvailabilityViews.length; j++) {
      if (
        otherAvailabilityViews[j][i] == CalendarAvailability.reserved ||
        otherAvailabilityViews[j][i] == CalendarAvailability.tentative
      ) {
        count++;
      }
      if (count + 1 > maxAvailableCount) {
        if (
          availabilityView[i] === CalendarAvailability.free ||
          availabilityView[i] === CalendarAvailability.assignedToOthers
        ) {
          availabilityView[i] = CalendarAvailability.maxUtilization;
        }
      }
    }
  }
  return scheduleHelper.patchAvailabilityViewByLimits(availabilityView, filter);
};

const getFreeInfo = (
  configService: ConfigService,
  filter: FloorplanFilter,
  availabilityArr: CalendarAvailability[]
) => {
  var start = startOfDay(filter.date);

  var pointer = filter.time;

  var timeIndex = Math.floor(differenceInMinutes(pointer, start) / 15);
  var durationCount = filter.duration
    ? Math.floor((filter.duration.getHours() * 60 + filter.duration.getMinutes()) / 15)
    : 1;

  let available = true;

  if (availabilityArr[timeIndex] == CalendarAvailability.reserved) {
    return {
      color: configService.value.design.background.reservableResource.reserved,
      available: false,
    };
  }

  const anyReserved = availabilityArr.find(
    (value, index) => index >= timeIndex && index < timeIndex + durationCount && value === CalendarAvailability.reserved
  );
  if (anyReserved) {
    return {
      color: configService.value.design.background.reservableResource.reserved,
      available: false,
    };
  }

  const anyClosed = availabilityArr.find(
    (value, index) => index >= timeIndex && index < timeIndex + durationCount && value === CalendarAvailability.closed
  );
  if (anyClosed) {
    return {
      color: configService.value.design.background.reservableResource.closed,
      available: false,
    };
  }

  const anyLimited = availabilityArr.find(
    (value, index) => index >= timeIndex && index < timeIndex + durationCount && value === CalendarAvailability.limited
  );
  if (anyLimited) {
    return {
      color: configService.value.design.background.reservableResource.limited,
      available: false,
    };
  }

  if (anyLimited) {
    return {
      color: configService.value.design.background.reservableResource.tentative,
      available: false,
    };
  }

  const anyFreeOutsideWorkingHours = availabilityArr.find(
    (value, index) =>
      index >= timeIndex && index < timeIndex + durationCount && value === CalendarAvailability.freeOutsideWorkingHours
  );
  if (anyFreeOutsideWorkingHours) {
    return {
      color: mixColors(configService.value.design.background.reservableResource.free, 'rgba(255,255,255,0.59)', 0.5),
      available: available,
    };
  }

  return {
    color: available
      ? configService.value.design.background.reservableResource.free
      : configService.value.design.background.reservableResource.limited,
    available: available,
  };
};

export const floorplans = (configService: ConfigService, translocoService: TranslocoService) =>
  createSelector(
    floorplanFilter,
    timelineSizes,
    storeData(),
    resourcesLoading,
    floorplanSchedules,
    getFloorplanRequirements,

    (
      _filter: FloorplanFilter,
      _sizes,
      _storeData,
      _packedResourcesLoading,
      _packedSchedules,
      __requirements,
      props
    ) => {
      const patchedSchedules = patchSchedules(
        !_packedSchedules.loading ? _packedSchedules.data : [],
        _filter,
        [..._storeData.resourcesData],
        []
      );

      let _requirements: any[] = [];

      const ids = _storeData.areas.data.map((x) => x.id);
      if (__requirements.length === 0) {
        const resourceTypeInfos = props.resoureTypeInfos as AssistResourceTypeInfoBM[];
        resourceTypeInfos.forEach((rstInfo, rIndex) => {
          _requirements.push({
            id: rIndex.toString(),
            filters: [],
            csCode: rstInfo.csCode,
            typeName: localized(rstInfo.name, translocoService),
            typeId: rstInfo.resourceTypeId,
          });
        });
      } else {
        _requirements = __requirements;
      }

      if (getLocationNames(_filter.name).length > 0) {
        _requirements = [
          {
            areas: ids,
            id: 'name',
            equipments: [],
            typeName: null,
            typeId: null,
          },
          ..._requirements,
        ];
      }

      const groupedResources: TimelineRoomGroup[] = _requirements.map((req, rindex) => ({
        requirement: req,
        height:
          patchedSchedules.filter((ps) => ps.requirementId === req.id).length *
          (_sizes.scheduleHeight + _sizes.scheduleMargin * 2),
        timelineSchedules: patchedSchedules
          .filter((ps) => ps.requirementId === req.id)
          .map((ps, psindex) => {
            const rNew = _storeData.resourcesData.find((x) => x.id == ps.resourceId);
            const areaId = rNew.mapInfos.length > 0 ? rNew.mapInfos[0].areaId : -1;
            const toSend = patchedSchedules
              .filter((ps) => ps.requirementId === req.id)
              .filter((sched) => {
                const rNewInner = _storeData.resourcesData.find((x) => x.id == sched.resourceId);
                const subAreaId = rNewInner.mapInfos.length > 0 ? rNewInner.mapInfos[0].areaId : -1;
                return sched.resourceId !== ps.resourceId && areaId == subAreaId;
              })
              .map((x) => ({
                availabilityView: x.availabilityView.split(''),
                scheduleId: x.scheduleId,
              }));

            const distinctSend: any[] = [];
            const map = new Map();
            for (const item of toSend) {
              if (!map.has(item.scheduleId)) {
                map.set(item.scheduleId, true); // set any value to Map
                distinctSend.push({
                  id: item.scheduleId,
                  availabilityView: item.availabilityView,
                });
              }
            }
            const overpatchedAvailabilitzView = getOverpatchedAvailabilityView(
              ps.availabilityView.split('') as CalendarAvailability[],
              _filter,
              distinctSend.map((x) => x.availabilityView),
              _storeData.areas.data.find((area) => area.id == areaId)
                ? _storeData.areas.data.find((area) => area.id == areaId).maxUt
                : 100
            );
            const resource = _storeData.resourcesData.find((r) => ps.resourceId.toUpperCase() === r.id.toUpperCase());
            const freeInfo = getFreeInfo(configService, _filter, overpatchedAvailabilitzView);
            const result = {
              yours: resource.reservedTo === 'you',
              others: resource.reservedTo === 'other',
              resourceRequirementId: req.id,
              resourceRequirement: req,
              resourceId: ps.resourceId,
              resource: resource,
              events: [],
              reservedTo: resource.reservedTo,
              freeClass: 'todo',
              freeColor: freeInfo.color,
              available: freeInfo.available,
              freePercentage: null,
              schedule: ps,
            };
            return result;
          })
          .filter(
            (x) => (!!x.resource && x.resource.reservationsEnabled === true) || x.resource.navigationEnabled === true
          ),
      }));

      const patchedGroupedRooms = groupedResources.map((gr, grindex) => ({
        ...gr,
        timelineSchedules: gr.timelineSchedules.map((ts, scheduleIndex) => ({
          ...ts,
          events: ts.events.map((e) => ({
            ...e,
            top:
              e.top +
              (_sizes.scheduleHeight + _sizes.scheduleMargin * 2) * scheduleIndex +
              groupedResources
                .filter((_, i) => i < grindex)
                .map((x) => x.height)
                .reduce((val, acc) => acc + val + (_sizes.groupDelimiterHeight - _sizes.scheduleMargin * 2), 0),
          })),
        })),
      }));

      const days: Day[] = daysBetweemDates(startOfDay(_filter.date), endOfDay(_filter.date)).map(
        (d) =>
          ({
            date: d,
            hours: [],
            sunday: false,
            today: false,
          }) as Day
      );

      const temp24 = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23];
      const startIsEnd = getDayOfYear(startOfDay(_filter.date)) === getDayOfYear(endOfDay(_filter.date));
      const hours24 = startIsEnd
        ? temp24.filter((h) => h >= getHours(startOfDay(_filter.date)) && h <= getHours(endOfDay(_filter.date)))
        : temp24;

      const startHours = hours24.filter((h) => h >= getHours(startOfDay(_filter.date)));
      const pim = hours24.filter((h) => h <= getHours(endOfDay(_filter.date)));
      const endHours = pim.filter((_, index) => index < pim.length - 1);
      days.forEach((day, index) => {
        // Hours
        const isStart = index === 0;
        const isEnd = index === days.length - 1;
        day.hours = isEnd ? endHours : isStart ? startHours : hours24;
      });

      let finalPatchedGroupAndSortedRooms: any[] = [];

      _requirements.forEach((requirement) => {
        const found = patchedGroupedRooms.filter((x) => x.requirement.id === requirement.id);
        finalPatchedGroupAndSortedRooms.push(...found);
      });

      var temp = days.map((day, index) => {
        const prevCount = days.filter((_it, iindex) => iindex < index).reduce((acc, it) => acc + it.hours.length, 0);
        return (prevCount + day.hours.length) * _sizes.quarterWidth * 4;
      });

      var backgroundString = temp.reduce((acc, it, index) => {
        return (
          acc +
          (index != 0 ? ',' : '') +
          ` linear-gradient(90deg,
      transparent ${it - 3}px,
      #233C5A ${it - 3}px,
      #233C5A ${it + 3}px,
      transparent ${it + 3}px)
      `
        );
      }, '');

      let selectedArea = _storeData.allAreasHierarchically.data[0];
      if (
        _filter.areaId !== null &&
        _filter.areaId !== undefined &&
        !_storeData.areas.loading &&
        _storeData.areas.data.length > 0
      ) {
        let pointer = _storeData.areas.data.find((x) => x.id === _filter.areaId);
        let ids = [pointer.id];

        while (pointer.parentAreaId != null) {
          pointer = _storeData.areas.data.find((x) => x.id === pointer.parentAreaId);
          ids.push(pointer.id);
        }

        ids = ids.reverse();
        let index = 0;
        pointer = _storeData.allAreasHierarchically.data.find((x) => x.id === ids[index]);
        while (pointer.id !== _filter.areaId) {
          index++;
          pointer = pointer.children.find((x) => x.id === ids[index]);
        }
        selectedArea = pointer;
      }

      let breadcrumb: any[] = [];
      if (selectedArea) {
        let pointer = selectedArea;
        do {
          breadcrumb = [
            {
              area: pointer,
              name: localized(pointer.name, translocoService),
            },
            ...breadcrumb,
          ];
          pointer = pointer.parentArea;
        } while (pointer != null);
      }

      return {
        loading: _packedResourcesLoading || _packedSchedules.loading,
        filter: _filter,
        selectedArea: selectedArea,
        breadcrumb: breadcrumb,
        groupedRooms: finalPatchedGroupAndSortedRooms,
        requirements: _requirements,
        sizes: _sizes,
        computed: {
          background: backgroundString,
          totallHeight: patchedGroupedRooms.map((x) => x.height).reduce((curr, acc) => curr + acc, 0),
          totallWidth:
            days.reduce((acc, day) => acc + day.hours.length * _sizes.quarterWidth * 4, 0) +
            (getMinutes(endOfDay(_filter.date)) / 15) * _sizes.quarterWidth,
          days: days,
        },
      };
    }
  );
